/**
 * Listas de valores + Secuencias.
 * Al hacer clic en "Ver secuencias" de una lista, muestra la tabla de secuencias de esa lista.
 */
import { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import CrudPage from './shared/CrudPage';
import { imageUrl, ContinueButton, YesNoButtons } from '../tests/shared/TestLayout';
import SafeHtml from '../tests/shared/SafeHtml';
import {
  getListas, getLista, createLista, updateLista, deleteLista,
  getSecuencias, createSecuencia, updateSecuencia, deleteSecuencia,
  uploadAdminFile, vincularImagenSecuencia,
} from '../../api/adminClient';

export default function AdminListas() {
  const [searchParams, setSearchParams] = useSearchParams();
  const listaIdParam = searchParams.get('listaId');
  const [selectedLista, setSelectedLista] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showLegacyUploader, setShowLegacyUploader] = useState(false);

  useEffect(() => {
    if (!listaIdParam) {
      setSelectedLista(null);
      return;
    }
    setLoading(true);
    getLista(Number(listaIdParam))
      .then((lista) => setSelectedLista({ ID: lista.id_listavalores, Descripcion: lista.Descripcion }))
      .catch(() => setSearchParams({}))
      .finally(() => setLoading(false));
  }, [listaIdParam]);

  if (loading) {
    return <div className="p-8 text-slate-500 text-sm">Cargando...</div>;
  }

  if (selectedLista) {
    return (
      <SecuenciasPage
        lista={selectedLista}
        onBack={() => setSearchParams({})}
      />
    );
  }

  return (
    <>
      <CrudPage
        title="Listas de valores"
        idKey="ID"
        columns={[
          { key: 'ID',          label: 'ID' },
          {
            key: 'Descripcion',
            label: 'Descripción',
            primary: true,
            render: (v, row) => (
              <div>
                <div>{v}</div>
                {(row.categorias || row.tareas) && (
                  <div className="font-normal text-xs text-slate-400 mt-0.5">
                    {[row.categorias, row.tareas].filter(Boolean).join(' · ')}
                  </div>
                )}
              </div>
            ),
          },
          {
            key: 'baja',
            label: 'Estado',
            render: (v) => v
              ? <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-600">De baja</span>
              : <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">Activa</span>,
          },
        ]}
        fields={[
          { key: 'Descripcion', label: 'Descripción', required: true },
          { key: 'baja', label: 'Baja', type: 'checkbox' },
        ]}
        fetchAll={getListas}
        onCreate={createLista}
        onCreated={(created) => setSearchParams({ listaId: String(created.id) })}
        onUpdate={updateLista}
        onDelete={deleteLista}
        actions={[
          { label: 'Ver secuencias', onClick: (row) => setSearchParams({ listaId: String(row.ID) }) },
        ]}
        titleExtra={
          <button
            type="button"
            onClick={() => setShowLegacyUploader(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-violet-300 text-violet-700 hover:bg-violet-50 text-sm font-medium"
          >
            Cargar imágenes legacy
          </button>
        }
      />
      {showLegacyUploader && <LegacyImagesUploader onClose={() => setShowLegacyUploader(false)} />}
    </>
  );
}

// Sube en lote imágenes legacy descargadas a mano y las vincula por id_secuencia,
// sin importar a qué Lista de valores pertenezcan:
//   d_{id}.png / i_{id}.png       -> rutaImagen1 / rutaImagen2 (sugerencia de ImageField)
const LEGACY_IMG_RE = /^([di])_(\d+)\.(png|jpe?g|gif|webp)$/i;
const LEGACY_IMG_CAMPO = { d: 'rutaImagen1', i: 'rutaImagen2' };
const CAMPO_LABEL = { rutaImagen1: 'Imagen izquierda', rutaImagen2: 'Imagen derecha' };

function LegacyImagesUploader({ onClose }) {
  const [matched, setMatched] = useState([]);   // [{ file, id, campo, name }]
  const [unmatched, setUnmatched] = useState([]); // [name]
  const [results, setResults] = useState({});   // name -> { status, message }
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);

  const handleFiles = (fileList) => {
    const files = Array.from(fileList);
    const nextMatched = [];
    const nextUnmatched = [];
    for (const file of files) {
      const mImg = file.name.match(LEGACY_IMG_RE);
      if (mImg) {
        const [, prefix, id] = mImg;
        nextMatched.push({ file, id: Number(id), campo: LEGACY_IMG_CAMPO[prefix.toLowerCase()], name: file.name });
        continue;
      }
      nextUnmatched.push(file.name);
    }
    setMatched(nextMatched);
    setUnmatched(nextUnmatched);
    setResults({});
    setDone(false);
  };

  const setResult = (name, result) =>
    setResults((r) => ({ ...r, [name]: result }));

  const procesarUno = async (item) => {
    setResult(item.name, { status: 'subiendo' });
    try {
      const up = await uploadAdminFile(item.file, `secuencias/${item.id}`, item.name);
      const link = await vincularImagenSecuencia(item.id, item.campo, up.url);
      setResult(item.name, { status: link.status });
    } catch (e) {
      setResult(item.name, { status: 'error', message: e.message });
    }
  };

  const procesarTodo = async () => {
    setRunning(true);
    setDone(false);
    const CONCURRENCY = 4;
    const queue = [...matched];
    const workers = Array.from({ length: CONCURRENCY }, async () => {
      while (queue.length) {
        const item = queue.shift();
        if (item) await procesarUno(item);
      }
    });
    await Promise.all(workers);
    setRunning(false);
    setDone(true);
  };

  const counts = matched.reduce((acc, m) => {
    const status = results[m.name]?.status ?? 'pendiente';
    acc[status] = (acc[status] ?? 0) + 1;
    return acc;
  }, {});

  const STATUS_LABEL = {
    pendiente:        { label: 'Pendiente',        cls: 'bg-slate-100 text-slate-500' },
    subiendo:         { label: 'Subiendo...',       cls: 'bg-blue-100 text-blue-700' },
    ok:               { label: 'Vinculada',         cls: 'bg-green-100 text-green-700' },
    ya_tenia_imagen:  { label: 'Ya tenía imagen',   cls: 'bg-amber-100 text-amber-700' },
    no_existe:        { label: 'ID no existe',      cls: 'bg-red-100 text-red-700' },
    error:            { label: 'Error',             cls: 'bg-red-100 text-red-700' },
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 border-b border-slate-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-slate-800">Cargar imágenes legacy</h2>
          <p className="text-sm text-slate-500 mt-1">
            Seleccioná todos los archivos descargados (nombrados <span className="font-mono">d_ID.png</span> / <span className="font-mono">i_ID.png</span>).
            Se suben a Firebase y se vinculan solos por ID de secuencia — sin pisar lo que ya esté cargado.
          </p>
        </div>

        <div className="px-6 py-5 overflow-y-auto flex-1 flex flex-col gap-4">
          <input
            type="file"
            multiple
            accept="image/*"
            disabled={running}
            onChange={(e) => handleFiles(e.target.files)}
            className="text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100 disabled:opacity-50"
          />

          {unmatched.length > 0 && (
            <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
              {unmatched.length} archivo{unmatched.length === 1 ? '' : 's'} no coincide{unmatched.length === 1 ? '' : 'n'} con el patrón d_ID / i_ID y se ignora{unmatched.length === 1 ? '' : 'n'}: {unmatched.slice(0, 8).join(', ')}{unmatched.length > 8 ? '…' : ''}
            </p>
          )}

          {matched.length > 0 && (
            <>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-violet-50 text-violet-700 font-medium">
                  {matched.length} reconocido{matched.length === 1 ? '' : 's'}
                </span>
                {Object.entries(counts).map(([status, n]) => (
                  <span key={status} className={`inline-flex items-center px-2.5 py-1 rounded-full font-medium ${STATUS_LABEL[status]?.cls ?? 'bg-slate-100 text-slate-500'}`}>
                    {STATUS_LABEL[status]?.label ?? status}: {n}
                  </span>
                ))}
              </div>

              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
                  {matched.map((m) => {
                    const r = results[m.name];
                    const status = r?.status ?? 'pendiente';
                    const info = STATUS_LABEL[status] ?? { label: status, cls: 'bg-slate-100 text-slate-500' };
                    return (
                      <div key={m.name} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                        <div className="min-w-0">
                          <div className="font-mono text-xs text-slate-700 truncate">{m.name}</div>
                          <div className="text-xs text-slate-400">Secuencia #{m.id} · {CAMPO_LABEL[m.campo] ?? m.campo}</div>
                        </div>
                        <span className={`shrink-0 text-[11px] px-2 py-0.5 rounded-full font-medium ${info.cls}`}>
                          {info.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {done && (
            <p className="text-sm text-slate-600">
              Terminado. Revisá arriba los casos "Ya tenía imagen" (no se pisaron) e "ID no existe" / "Error" (no se pudieron vincular).
            </p>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm hover:bg-slate-50"
          >
            Cerrar
          </button>
          <button
            onClick={procesarTodo}
            disabled={running || matched.length === 0}
            className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium disabled:opacity-50"
          >
            {running ? 'Subiendo...' : `Subir y vincular (${matched.length})`}
          </button>
        </div>
      </div>
    </div>
  );
}

// Preview en texto plano del Valor (que ahora puede ser HTML enriquecido) para no romper
// el layout de la tabla con alineaciones/colores — el formato completo se ve en el form y en el test.
function stripHtml(html) {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || '';
}

// Deriva la URL del thumbnail 64×64 generado al momento de subir.
// Si la imagen no es de Firebase o el thumb no existe, onError carga el original.
function toThumbUrl(url) {
  if (!url) return url;
  const m = url.match(/\/o\/(.+?)\?alt=media/);
  if (!m) return url;
  const decoded = decodeURIComponent(m[1]);          // "secuencias/96/img_abc.png"
  const dir      = decoded.substring(0, decoded.lastIndexOf('/'));
  const file     = decoded.substring(decoded.lastIndexOf('/') + 1);
  const base     = file.substring(0, file.lastIndexOf('.'));
  const thumbPath = `${dir}/thumbs/${base}.png`;
  return url.replace(m[1], thumbPath.split('/').map(encodeURIComponent).join('%2F'));
}

function SecuenciasPage({ lista, onBack }) {
  const fetchAll = useCallback(() => getSecuencias(lista.ID), [lista.ID]);
  const onCreate = useCallback((d) => createSecuencia(lista.ID, d), [lista.ID]);
  const [previewRow, setPreviewRow] = useState(null);

  return (
    <div className="h-full flex flex-col">
      <div className="flex-shrink-0 px-6 pt-6 pb-0">
        <button
          onClick={onBack}
          className="text-sm text-violet-600 hover:text-violet-800 bg-transparent border-0 cursor-pointer mb-2"
        >
          ← Volver a listas
        </button>
      </div>
      <div className="flex-1 min-h-0">
      <CrudPage
        title={`Secuencias — ${lista.Descripcion}`}
        idKey="ID"
        columns={[
          { key: 'ID', label: 'ID' },
          { key: 'Nro_Secuencia', label: 'Nro' },
          {
            key: 'Valor',
            label: 'Valor',
            primary: true,
            render: (v, row) => {
              const tags = [
                { label: 'Consigna',    active: row.es_consigna,         cls: 'bg-violet-100 text-violet-700 border border-violet-700 font-bold' },
                { label: 'Bool',        active: row.es_pregunta_boolena, cls: 'bg-blue-100 text-blue-700 border border-blue-700 font-bold' },
                { label: 'Verdadero',   active: row.es_verdadero,        cls: 'bg-green-100 text-green-700 border border-green-700 font-bold' },
                { label: `Rot. ${row.rotacion ?? 0}°`, active: !!row.rotacion, cls: 'bg-amber-100 text-amber-700 border border-amber-700 font-bold' },
                { label: 'Facilitador', active: row.facilitador,         cls: 'bg-purple-100 text-purple-700 border border-purple-700 font-bold' },
              ];
              return (
                <div>
                  <div>{stripHtml(v)}</div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {tags.map((t) => (
                      <span
                        key={t.label}
                        className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full ${t.active ? t.cls : 'bg-slate-100 text-slate-400 line-through'}`}
                      >
                        {t.label}
                      </span>
                    ))}
                  </div>
                </div>
              );
            },
          },
          { key: 'respuesta', label: 'Respuesta' },
          {
            key: 'rutaImagen1',
            label: 'Img izquierda',
            render: (v) => v ? <img src={toThumbUrl(v)} onError={(e) => { e.target.src = v; }} className="h-16 w-16 object-contain rounded" /> : '',
          },
          {
            key: 'rutaImagen2',
            label: 'Img derecha',
            render: (v) => v ? <img src={toThumbUrl(v)} onError={(e) => { e.target.src = v; }} className="h-16 w-16 object-contain rounded" /> : '',
          },
        ]}
        modalCols={2}
        fields={[
          { key: 'Valor',            label: 'Valor',             type: 'richtext', required: true, colSpan: 2 },
          {
            key: 'Nro_Secuencia', label: 'Nro. secuencia', type: 'number',
            defaultValue: (rows) => rows.reduce((max, r) => Math.max(max, r.Nro_Secuencia || 0), 0) + 1,
          },
          { key: 'rotacion',         label: 'Rotación (grados)', type: 'number', defaultValue: 0 },
          { key: 'respuesta',        label: 'Respuesta esperada' },
          { key: 'valor_enmascardo', label: 'Valor enmascarado' },
          {
            key: '_flags', label: 'Flags', type: 'flag-group',
            items: [
              { key: 'es_consigna',         label: 'Consigna' },
              { key: 'es_pregunta_boolena', label: 'Preg. booleana' },
              { key: 'es_verdadero',        label: 'Verdadero' },
              { key: 'es_secuencia',        label: 'Es secuencia' },
              { key: 'facilitador',         label: 'Facilitador' },
              { key: 'es_ejemplo',          label: 'Ejemplo' },
            ],
          },
          {
            key: 'rutaImagen1',
            label: 'Imagen izquierda',
            type: 'image',
            onUpload: (file) => uploadAdminFile(file, `secuencias/${lista.ID}`),
            hint: (row) => row?.ID ? `d_${row.ID}.png` : null,
          },
          {
            key: 'rutaImagen2',
            label: 'Imagen derecha',
            type: 'image',
            onUpload: (file) => uploadAdminFile(file, `secuencias/${lista.ID}`),
            hint: (row) => row?.ID ? `i_${row.ID}.png` : null,
          },
        ]}
        fetchAll={fetchAll}
        onCreate={onCreate}
        onUpdate={updateSecuencia}
        onDelete={deleteSecuencia}
        actions={[
          { label: 'Vista previa', onClick: setPreviewRow },
        ]}
      />
      </div>

      {previewRow && <PreviewModal row={previewRow} onClose={() => setPreviewRow(null)} />}
    </div>
  );
}

const PREVIEW_DEVICES = {
  pc:      { label: 'PC',      icon: '🖥️' },
  celular: { label: 'Celular', icon: '📱', width: 390 }, // ancho real de iPhone 12/13/14, la resolución más común
};

// Modal de previsualización a tamaño real (no una miniatura escalada): reproduce el mismo
// layout/estilos que TestRunLibreTest usa para un estímulo genérico, para ver de un vistazo
// cómo se va a ver ese ítem sin tener que correr el test completo. El selector PC/Celular
// y el botón de cerrar flotan centrados arriba, sobre el contenido.
function PreviewModal({ row, onClose }) {
  const [device, setDevice] = useState('pc');
  const width = PREVIEW_DEVICES[device].width;

  return (
    <div className="fixed inset-0 z-50 bg-slate-300 overflow-y-auto">
      <div className="fixed top-4 left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-1 bg-white/95 backdrop-blur rounded-full shadow-lg border border-slate-200 px-1.5 py-1.5">
        {Object.entries(PREVIEW_DEVICES).map(([key, d]) => (
          <button
            key={key}
            type="button"
            onClick={() => setDevice(key)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              device === key
                ? 'bg-violet-600 text-white'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {d.icon} {d.label}
          </button>
        ))}
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-1.5 rounded-full text-sm font-medium text-red-600 hover:bg-red-50"
        >
          Cerrar
        </button>
      </div>

      <div
        className={`bg-white mx-auto ${device === 'celular' ? 'shadow-2xl' : ''}`}
        style={{ width: width ?? '100%', maxWidth: '100%' }}
      >
        <StimulusPreview row={row} />
      </div>
    </div>
  );
}

// Réplica no interactiva de TestLayout + los 4 bloques de estímulo genéricos
// (consigna / pregunta booleana / con respuesta / sin respuesta) usados en LibreTest,
// el tipo de test más representativo. No simula temporizadores ni mecánica real de cada
// tipo (eso varía por tipo de test); es una foto de cómo se ve el contenido del ítem.
function StimulusPreview({ row }) {
  const isConsigna = !!row.es_consigna;
  const isBool = !!row.es_pregunta_boolena;
  const hasAnswer = row.respuesta != null && row.respuesta !== '';
  const hasImages = !!(row.rutaImagen1 || row.rutaImagen2);

  const images = hasImages && (
    <div className="flex gap-8 items-center justify-center flex-wrap mb-6">
      {row.rutaImagen1 && <img src={imageUrl(row.rutaImagen1)} alt="" className="max-h-56 object-contain" />}
      {row.rutaImagen2 && <img src={imageUrl(row.rutaImagen2)} alt="" className="max-h-56 object-contain" />}
    </div>
  );

  return (
    <div className="min-h-screen bg-white text-slate-800 relative flex flex-col items-center justify-center p-6">
      <div className="absolute top-0 left-0 right-0 bg-slate-100 border-b border-slate-200 py-3 px-4 flex justify-between items-center text-sm text-slate-500">
        <span>Nro. {row.Nro_Secuencia ?? '—'}</span>
        <span className="text-slate-400">Salir</span>
      </div>

      {isConsigna ? (
        <>
          <SafeHtml
            as="p"
            html={row.Valor}
            className="text-center text-slate-800 whitespace-pre-wrap leading-tight max-w-3xl mb-4"
            style={{ fontSize: '33px', fontWeight: 700 }}
          />
          {images}
          <ContinueButton onClick={() => {}} />
        </>
      ) : isBool ? (
        <>
          <div className="text-3xl font-medium text-center mb-4 text-slate-800 max-w-3xl">
            {row.rutaImagen1 && <img src={imageUrl(row.rutaImagen1)} alt="" className="max-h-48 mx-auto mb-4 object-contain" />}
            <SafeHtml as="span" html={row.Valor} />
          </div>
          <YesNoButtons onYes={() => {}} onNo={() => {}} />
        </>
      ) : (
        <>
          <SafeHtml as="div" html={row.Valor} className="text-4xl font-bold text-center mb-6 text-slate-800" />
          {images}
          {hasAnswer && (
            <input
              type="text"
              disabled
              placeholder="Ingrese su respuesta"
              className="max-w-md w-full rounded-lg bg-white border border-slate-300 text-slate-800 p-4 text-center text-lg outline-none"
            />
          )}
        </>
      )}
    </div>
  );
}
